const MEGABYTE = 1024 * 1024;
const THUMBNAIL_SUFFIX = '.thumb.jpg';

export const MAX_UPLOAD_BYTES = 200 * MEGABYTE;
export const MAX_CREATIVES_PER_BATCH = 50;

export const UPLOAD_CONTENT_TYPES = ['video/mp4', 'image/png', 'image/jpeg'] as const;

// Maps, not objects: an object index answers for `constructor` and `__proto__`
// too, which would let either lookup escape the allowlist.
const EXTENSIONS = new Map<UploadContentType, { extension: string; aliases: string[] }>([
  ['video/mp4', { extension: 'mp4', aliases: [] }],
  ['image/png', { extension: 'png', aliases: [] }],
  ['image/jpeg', { extension: 'jpg', aliases: ['jpeg'] }],
]);

const CONTENT_TYPES = new Map<string, UploadContentType>(
  [...EXTENSIONS].flatMap(([contentType, { extension, aliases }]) =>
    [extension, ...aliases].map((name): [string, UploadContentType] => [name, contentType]),
  ),
);

export type UploadContentType = (typeof UPLOAD_CONTENT_TYPES)[number];

export type UploadCheck =
  | { ok: true; contentType: UploadContentType }
  | { ok: false; reason: string };

export interface UploadRequest {
  contentType: string;
  sizeBytes: number;
}

export function isUploadContentType(contentType: string): contentType is UploadContentType {
  return EXTENSIONS.has(contentType as UploadContentType);
}

export function contentTypeOfFileName(fileName: string): UploadContentType | null {
  const dot = fileName.lastIndexOf('.');
  if (dot === -1) return null;
  return CONTENT_TYPES.get(fileName.slice(dot + 1).toLowerCase()) ?? null;
}

export function uploadObjectName(contentType: UploadContentType, id: string): string {
  return `${id}.${EXTENSIONS.get(contentType)?.extension}`;
}

export function thumbnailObjectName(objectName: string): string {
  const dot = objectName.lastIndexOf('.');
  const slash = objectName.lastIndexOf('/');
  if (dot === -1 || dot < slash) return `${objectName}${THUMBNAIL_SUFFIX}`;
  return `${objectName.slice(0, dot)}${THUMBNAIL_SUFFIX}`;
}

export function gsUri(bucket: string, objectName: string): string {
  return `gs://${bucket}/${objectName}`;
}

export function parseGsUri(uri: string): { bucket: string; objectName: string } | null {
  if (!uri.startsWith('gs://')) return null;
  const rest = uri.slice('gs://'.length);
  const slash = rest.indexOf('/');
  if (slash <= 0 || slash === rest.length - 1) return null;
  return { bucket: rest.slice(0, slash), objectName: rest.slice(slash + 1) };
}

export function validateUpload({ contentType, sizeBytes }: UploadRequest): UploadCheck {
  if (!isUploadContentType(contentType)) {
    return { ok: false, reason: `${contentType} is not an mp4, png or jpeg` };
  }
  if (sizeBytes <= 0) {
    return { ok: false, reason: 'the file is empty' };
  }
  if (sizeBytes > MAX_UPLOAD_BYTES) {
    return { ok: false, reason: `the file is over the ${MAX_UPLOAD_BYTES / MEGABYTE}MB limit` };
  }
  return { ok: true, contentType };
}
